import React, { useState } from 'react';
import type { FileDetail } from '../types';

interface Props {
  file: FileDetail;
  onClose: () => void;
  onSaveAcl: (
    acls: Array<{ scope_type: string; scope_id: string; permissions: string }>
  ) => Promise<void>;
}

export const AclManageModal: React.FC<Props> = ({ file, onClose, onSaveAcl }) => {
  const [aclList, setAclList] = useState<
    Array<{ scope_type: string; scope_id: string; permissions: string }>
  >(
    file.acls.length > 0
      ? file.acls.map((a) => ({
          scope_type: a.scope_type,
          scope_id: a.scope_id,
          permissions: a.permissions,
        }))
      : [
          { scope_type: 'role', scope_id: 'SYSTEM_OWNER', permissions: 'view,download,upload,version,archive,manage_acl' },
          { scope_type: 'role', scope_id: 'OPERATIONS_PARTNER', permissions: 'view,download' },
        ]
  );
  const [isSaving, setIsSaving] = useState(false);

  const availableRoles = [
    'SYSTEM_OWNER',
    'OPERATIONS_PARTNER',
    'MENTOR_PARTNER',
    'DEPARTMENT_HEAD',
    'DEPARTMENT_ASSISTANT',
    'ADVISOR',
    'LEARNER',
    'AUTHOR',
    'EDITOR',
    'PROJECT_MEMBER',
  ];

  const handleAddRole = (role: string) => {
    if (aclList.some((a) => a.scope_id === role)) return;
    setAclList((prev) => [
      ...prev,
      { scope_type: 'role', scope_id: role, permissions: 'view,download' },
    ]);
  };

  const handleRemoveAcl = (index: number) => {
    setAclList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTogglePerm = (index: number, perm: string) => {
    setAclList((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const currentPerms = item.permissions.split(',').map((p) => p.trim()).filter(Boolean);
        const nextPerms = currentPerms.includes(perm)
          ? currentPerms.filter((p) => p !== perm)
          : [...currentPerms, perm];
        return {
          ...item,
          permissions: nextPerms.join(','),
        };
      })
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSaveAcl(aclList);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const allPerms = ['view', 'download', 'upload', 'version', 'archive', 'manage_acl'];

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="acl-manage-modal">
      <div className="modal-window" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '680px' }}>
        <div className="modal-header">
          <div>
            <h3>Управление доступом</h3>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
              {file.display_name} ({file.file_id})
            </span>
          </div>
          <button type="button" className="file-hub-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ fontSize: '0.85rem', color: '#cbd5e1', margin: 0 }}>
            Настройте ролевые разрешения на просмотр, скачивание и модификацию документа в соответствии с политикой безопасности платформы.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {aclList.map((entry, idx) => {
              const currentPerms = entry.permissions.split(',').map((p) => p.trim());
              return (
                <div
                  key={idx}
                  style={{
                    background: 'rgba(30, 41, 59, 0.4)',
                    border: '1px solid rgba(148, 163, 184, 0.15)',
                    borderRadius: '10px',
                    padding: '0.85rem 1rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                    <span style={{ fontWeight: 600, color: '#38bdf8', fontSize: '0.88rem' }}>
                      {entry.scope_id}
                    </span>
                    <button
                      type="button"
                      className="action-icon-btn"
                      style={{ color: '#f87171' }}
                      onClick={() => handleRemoveAcl(idx)}
                    >
                      Удалить
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {allPerms.map((perm) => (
                      <label
                        key={perm}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          fontSize: '0.78rem',
                          color: '#cbd5e1',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={currentPerms.includes(perm)}
                          onChange={() => handleTogglePerm(idx, perm)}
                        />
                        {perm}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Добавить роль:</span>
            <select
              className="library-select"
              onChange={(e) => {
                if (e.target.value) {
                  handleAddRole(e.target.value);
                  e.target.value = '';
                }
              }}
              defaultValue=""
            >
              <option value="" disabled>
                Выберите роль...
              </option>
              {availableRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="file-hub-action-btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn-open-preview"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Сохранение...' : 'Сохранить права доступа'}
          </button>
        </div>
      </div>
    </div>
  );
};
